import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography, CardActionArea, Skeleton } from '@mui/material';
import { GlassCard } from '../../../components/ui';
import { getCategoryImage } from '../../../config/wikiCategoryImagesConfig';
import { BG_COLORS, TEXT_COLORS } from '../../../utils/colors';
import { WIKI_CATEGORY_CARD } from '../../../utils/responsive';

export interface WikiCategoryCardProps {
  name: string;
  source: string;
  path: string;
  /**
   * Высота карточки (px), посчитанная сеткой: min(естественная 5/6, влезающая в экран).
   * Когда места мало (низкое окно, зум, панель закладок) карточка «садится», картинка
   * подрезается по вертикали, а название остаётся видимым — вместо обрезки карточки краем подложки.
   * null — ещё не измерено: работаем по aspect-ratio, как раньше.
   */
  height?: number | null;
  hierarchyColor?: string;
  hierarchyDepth?: number;
  hierarchyHasChildren?: boolean;
  hierarchyIsRoot?: boolean;
}

const WikiCategoryCard: React.FC<WikiCategoryCardProps> = ({
  name,
  source,
  path,
  height,
  hierarchyColor,
  hierarchyHasChildren,
  hierarchyIsRoot,
}) => {
  const imagePath = getCategoryImage(source);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);

  return (
    <GlassCard
      sx={{
        width: '100%',
        // aspectRatio задаём ТОЛЬКО когда высота не измерена: пара `aspectRatio` + ограничение по высоте
        // ужимает карточке ещё и ширину (браузер сохраняет пропорцию за счёт inline-размера).
        ...(height ? { height } : { aspectRatio: '5 / 6' }),
        overflow: 'hidden',
        // GPU optimization to prevent pixel artifacts on hover
        willChange: 'transform',
        backfaceVisibility: 'hidden',
        transform: 'translateZ(0)',
        '&:hover': {
          transform: 'translateY(-6px) translateZ(0)',
          borderColor: 'rgba(185, 28, 28, 0.6)',
          background: BG_COLORS.GLASS_LIGHTER,
          boxShadow: 'none',
        },
      }}
    >
      <CardActionArea
        component={RouterLink}
        to={path}
        sx={{
          height: '100%',
          position: 'relative',
          // Минимальная высота чтобы не схлопывалось при загрузке.
          // Когда высоту задала сетка — она уже с полом (MIN_CARD_HEIGHT), свой минимум не навязываем.
          ...(height ? {} : { minHeight: { xs: 120, sm: 140, md: 160 } }),
          '& .MuiCardActionArea-focusHighlight': {
            background: 'transparent',
          },
        }}
      >
        {/* Colored accent strip for hierarchy professions */}
        {hierarchyColor && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: hierarchyColor,
              zIndex: 2,
            }}
          />
        )}

        {/* Skeleton пока картинка грузится */}
        {!imgLoaded && !imgError && (
          <Skeleton
            variant="rectangular"
            animation="wave"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              bgcolor: 'rgba(255, 255, 255, 0.05)',
            }}
          />
        )}

        {/* Изображение на всю карточку */}
        {!imgError ? (
          <Box
            component="img"
            src={imagePath}
            alt={name}
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgError(true)}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              // Смещение кадра вверх: при сильном сжатии карточки центр композиции (лицо/предмет)
              // остаётся в кадре, обрезается низ.
              objectPosition: 'center 35%',
              transition: 'transform 0.3s ease, opacity 0.3s ease, filter 0.3s ease',
              opacity: imgLoaded ? 1 : 0,
              // Все картинки приглушены; активная (hover / клавиатурный фокус / тап) — разгорается
              filter: 'brightness(0.45) saturate(0.85)',
              // GPU optimization to prevent pixel artifacts on hover
              willChange: 'transform',
              backfaceVisibility: 'hidden',
              transform: 'translateZ(0)',
              '.MuiCardActionArea-root:hover &, .MuiCardActionArea-root:focus-visible &, .MuiCardActionArea-root:active &': {
                transform: 'scale(1.05) translateZ(0)',
                filter: 'brightness(1.05) saturate(1)',
              },
            }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              background: 'linear-gradient(135deg, rgba(185, 28, 28, 0.3) 0%, rgba(30, 30, 40, 0.8) 100%)',
            }}
          />
        )}

        {/* Название поверх картинки снизу */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            p: WIKI_CATEGORY_CARD.padding,
            background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 100%)',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            minHeight: '50%',
          }}
        >
          <Typography
            variant="subtitle1"
            component="h3"
            sx={{
              fontWeight: 600,
              fontSize: WIKI_CATEGORY_CARD.titleFontSize,
              textAlign: 'center',
              color: TEXT_COLORS.PRIMARY,
              lineHeight: 1.3,
              textShadow: '0 2px 4px rgba(0,0,0,0.8)',
            }}
          >
            {hierarchyColor && (
              <Box
                component="span"
                sx={{
                  color: hierarchyColor,
                  mr: 0.5,
                  fontSize: 'inherit',
                  textShadow: `0 0 8px ${hierarchyColor}`,
                }}
              >
                {hierarchyIsRoot && hierarchyHasChildren ? '◆' : '◇'}
              </Box>
            )}
            {name}
          </Typography>
        </Box>
      </CardActionArea>
    </GlassCard>
  );
};

export default WikiCategoryCard;
